import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { adminAPI } from '../../services/api'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Badge from '../../components/ui/Badge'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'
import {
  MagnifyingGlassIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  TrashIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline'

const UsersPage = () => {
  const { t } = useTranslation('admin')
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [confirm, setConfirm] = useState(null)

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const searchTimer = React.useRef(null)
  const handleSearch = (value) => {
    setSearch(value)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(value)
      setPage(1)
    }, 300)
  }

  const { data, isLoading } = useQuery(
    ['admin-users', { search: debouncedSearch, role: roleFilter, is_active: statusFilter, page }],
    async () => {
      const params = { page, limit: 20 }
      if (debouncedSearch) params.search = debouncedSearch
      if (roleFilter !== 'all') params.role = roleFilter
      if (statusFilter !== 'all') params.is_active = statusFilter === 'active'
      const response = await adminAPI.getUsers(params)
      return response.data
    }
  )

  const updateMutation = useMutation(
    ({ id, ...body }) => adminAPI.updateUser(id, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-users'])
        toast.success(t('users.updated'))
        setConfirm(null)
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || t('users.updateError'))
        setConfirm(null)
      },
    }
  )

  const deleteMutation = useMutation(
    (id) => adminAPI.deleteUser(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-users'])
        toast.success(t('users.deletedPermanently'))
        setConfirm(null)
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || t('users.updateError'))
        setConfirm(null)
      },
    }
  )

  const users = data?.users || []
  const pagination = data?.pagination || { total: 0, page: 1, totalPages: 1 }

  const isSelf = (userId) => userId === currentUser?.id

  const handleConfirm = () => {
    if (!confirm) return
    const { type, user } = confirm
    if (type === 'role') {
      updateMutation.mutate({ id: user.id, role: user.role === 'admin' ? 'user' : 'admin' })
    } else if (type === 'status') {
      updateMutation.mutate({ id: user.id, is_active: !user.is_active })
    } else if (type === 'delete') {
      deleteMutation.mutate(user.id)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return t('users.never')
    return new Date(dateStr).toLocaleDateString('pl-PL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    })
  }

  const getInitials = (user) => {
    if (user.first_name && user.last_name) {
      return (user.first_name[0] + user.last_name[0]).toUpperCase()
    }
    return (user.username || user.email)[0].toUpperCase()
  }

  const getName = (user) => {
    if (user.first_name || user.last_name) {
      return [user.first_name, user.last_name].filter(Boolean).join(' ')
    }
    return user.username
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-[var(--color-text)]">
          {t('users.title')}
        </h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          {t('users.subtitle')}
        </p>
      </div>

      {/* Filters */}
      <Card>
        <Card.Body>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-text-faint)]" />
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={t('users.searchPlaceholder')}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] text-sm outline-none focus:border-[#1f9f93]"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1) }}
              className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] text-sm"
            >
              <option value="all">{t('users.filterRole')}: {t('users.all')}</option>
              <option value="admin">{t('users.roleAdmin')}</option>
              <option value="user">{t('users.roleUser')}</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
              className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] text-sm"
            >
              <option value="all">{t('users.filterStatus')}: {t('users.all')}</option>
              <option value="active">{t('users.active')}</option>
              <option value="inactive">{t('users.inactive')}</option>
            </select>
            <span className="text-sm font-medium text-[var(--color-text-muted)]">
              {t('users.totalUsers', { count: pagination.total })}
            </span>
          </div>
        </Card.Body>
      </Card>

      {/* Users Table */}
      <Card>
        <Card.Body style={{ padding: 0 }}>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-[var(--color-text-faint)]">
              {t('users.noUsers')}
            </div>
          ) : (
            <div>
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '36px' }} />
                  <col />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '90px' }} />
                  <col style={{ width: '90px' }} />
                  <col style={{ width: '110px' }} />
                </colgroup>
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left px-2 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">
                      #
                    </th>
                    <th className="text-left px-2 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">
                      {t('users.filterRole') === 'Rola' ? 'Użytkownik' : 'User'}
                    </th>
                    <th className="text-left px-2 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">
                      {t('users.filterRole')}
                    </th>
                    <th className="text-left px-2 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">
                      {t('users.stats')}
                    </th>
                    <th className="text-left px-2 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">
                      {t('users.lastLogin')}
                    </th>
                    <th className="text-left px-2 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">
                      {t('users.registered')}
                    </th>
                    <th className="text-right px-2 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">
                      {t('users.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => (
                    <tr
                      key={u.id}
                      className="transition-colors hover:bg-[var(--color-hover)] cursor-pointer border-b border-[var(--color-border)]"
                      onClick={() => navigate(`/admin/users/${u.id}`)}
                    >
                      {/* # */}
                      <td className="px-2 py-3 text-xs font-medium text-[var(--color-text-faint)]">
                        {(page - 1) * 20 + idx + 1}
                      </td>

                      {/* User info */}
                      <td className="px-2 py-3 overflow-hidden">
                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ background: isSelf(u.id) ? '#1f9f93' : 'var(--color-text-faint)' }}
                          >
                            {getInitials(u)}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-sm truncate text-[var(--color-text)]">
                              {getName(u)}
                              {isSelf(u.id) && (
                                <span className="ml-1 text-xs font-medium px-1 py-0.5 rounded" style={{ background: 'rgba(31,159,147,0.12)', color: '#1f9f93' }}>
                                  Ty
                                </span>
                              )}
                            </div>
                            <div className="text-xs truncate text-[var(--color-text-muted)]">
                              {u.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td className="px-2 py-3">
                        <Badge variant={u.role === 'admin' ? 'primary' : 'secondary'} size="sm">
                          {u.role === 'admin' ? t('users.roleAdmin') : t('users.roleUser')}
                        </Badge>
                      </td>

                      {/* Stats - saldo */}
                      <td className="px-2 py-3">
                        {(() => {
                          const balance = (u.stats?.incomes_total ?? 0) - (u.stats?.expenses_total ?? 0)
                          const isPositive = balance >= 0
                          return (
                            <span
                              className="text-sm font-semibold whitespace-nowrap"
                              style={{ color: isPositive ? '#15803d' : '#ef4444' }}
                            >
                              {isPositive ? '+' : ''}{balance.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                            </span>
                          )
                        })()}
                      </td>

                      {/* Last login */}
                      <td className="px-2 py-3 text-xs whitespace-nowrap text-[var(--color-text-muted)]">
                        {formatDate(u.last_login)}
                      </td>

                      {/* Registered */}
                      <td className="px-2 py-3 text-xs whitespace-nowrap text-[var(--color-text-muted)]">
                        {formatDate(u.createdAt)}
                      </td>

                      {/* Actions */}
                      <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {/* Edit */}
                          <button
                            onClick={() => navigate(`/admin/users/${u.id}`)}
                            className="p-1.5 rounded-lg transition-colors"
                            style={{ color: '#1f9f93' }}
                            title={t('users.viewDetails')}
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>

                          {/* Ban/Unban */}
                          <button
                            disabled={isSelf(u.id)}
                            onClick={() => setConfirm({ type: 'status', user: u })}
                            className="p-1.5 rounded-lg transition-colors disabled:opacity-30"
                            style={{ color: u.is_active ? 'var(--color-text-muted)' : '#ef4444' }}
                            title={u.is_active ? t('users.deactivate') : t('users.activate')}
                          >
                            {u.is_active ? <NoSymbolIcon className="h-5 w-5" /> : <CheckCircleIcon className="h-5 w-5" />}
                          </button>

                          {/* Delete */}
                          <button
                            disabled={isSelf(u.id)}
                            onClick={() => setConfirm({ type: 'delete', user: u })}
                            className="p-1.5 rounded-lg transition-colors disabled:opacity-30"
                            style={{ color: '#ef4444' }}
                            title={t('users.deleteUser')}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            &laquo;
          </Button>
          <span className="flex items-center px-3 text-sm font-medium text-[var(--color-text-muted)]">
            {page} / {pagination.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pagination.totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            &raquo;
          </Button>
        </div>
      )}

      {/* Confirmation dialog */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-[var(--color-surface)] rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold mb-3 text-[var(--color-text)]">
              {confirm.type === 'delete' ? t('users.deleteUser') : confirm.type === 'status'
                ? (confirm.user.is_active ? t('users.deactivate') : t('users.activate'))
                : (confirm.user.role === 'admin' ? t('users.makeUser') : t('users.makeAdmin'))}
            </h3>
            <p className="text-sm mb-6 text-[var(--color-text-muted)]">
              {confirm.type === 'delete'
                ? t('users.confirmDelete', { name: getName(confirm.user) })
                : confirm.type === 'status'
                  ? t('users.confirmDeactivate', { name: getName(confirm.user) })
                  : t('users.confirmRoleChange', {
                      name: getName(confirm.user),
                      role: confirm.user.role === 'admin' ? t('users.roleUser') : t('users.roleAdmin'),
                    })}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" size="sm" onClick={() => setConfirm(null)}>
                Anuluj
              </Button>
              <Button
                variant={confirm.type === 'delete' ? 'error' : 'primary'}
                size="sm"
                loading={updateMutation.isLoading || deleteMutation.isLoading}
                onClick={handleConfirm}
              >
                {confirm.type === 'delete' ? t('users.deleteUser') : 'Potwierdź'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UsersPage
